/**
 * LinkedProjectsSection - Which other projects a project's threads may delegate
 * work into.
 *
 * A product that lives in two repositories — an API and the mobile app that
 * calls it — is one piece of work, and an agent opened on either half has to be
 * able to hand the other half to a subagent. This panel is where that
 * possibility gets declared.
 *
 * **Toggles, not a form.** Links are a set of ids, so the whole editor is one
 * checkbox per candidate project, saved on the spot: there is nothing to
 * validate client-side that the server does not validate anyway, and a
 * Save button over a list of checkboxes only adds a state to get wrong.
 *
 * **Directed.** The rows show what *this* project may reach; the reverse link
 * is a separate declaration on the other project. The panel says so rather than
 * mirroring the toggle, because "the app may also reach the backend" is a
 * decision somebody should make on purpose.
 *
 * Gated on `p.edit` per project, which is a project permission — so, like
 * `LocalCommandsSection`, the nav item carries no global permission and this
 * component asks the question per row instead.
 */
import type { ProjectId } from "@taskin-code/contracts";
import { useState } from "react";

import { useHasProjectPermission } from "../../auth/authStore";
import { newCommandId } from "../../lib/utils";
import { readNativeApi } from "../../nativeApi";
import { useStore } from "../../store";
import type { Project } from "../../types";
import { Switch } from "../ui/switch";

function ProjectLinksCard(props: {
  readonly project: Project;
  readonly candidates: ReadonlyArray<Project>;
  /** Suppressed when the surrounding screen already names the project. */
  readonly hideName?: boolean;
}) {
  const { project, candidates } = props;
  const canEdit = useHasProjectPermission(project.id as ProjectId, "p.edit");
  const [pendingId, setPendingId] = useState<ProjectId | null>(null);
  const [error, setError] = useState<string | null>(null);

  const linked = new Set(project.linkedProjectIds);

  const toggle = async (candidateId: ProjectId, next: boolean) => {
    const nextLinked = next
      ? [...project.linkedProjectIds, candidateId]
      : project.linkedProjectIds.filter((id) => id !== candidateId);
    const api = readNativeApi();
    if (!api) {
      setError("No hay conexión con el servidor.");
      return;
    }
    setPendingId(candidateId);
    setError(null);
    try {
      await api.orchestration.dispatchCommand({
        type: "project.meta.update",
        commandId: newCommandId(),
        projectId: project.id as ProjectId,
        linkedProjectIds: nextLinked,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar el vínculo.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="rounded-md border border-border/60 bg-card/40 p-4">
      {props.hideName === true ? null : (
        <div className="min-w-0">
          <h4 className="truncate text-sm font-semibold">{project.name}</h4>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{project.cwd}</p>
        </div>
      )}

      {candidates.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          No hay otros proyectos con los que vincular este.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {candidates.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-center justify-between gap-3 rounded border border-border/40 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate">{candidate.name}</span>
              <Switch
                checked={linked.has(candidate.id)}
                disabled={!canEdit || pendingId !== null}
                onCheckedChange={(checked) => void toggle(candidate.id as ProjectId, checked)}
                aria-label={`Permitir que los hilos de ${project.name} trabajen en ${candidate.name}`}
              />
            </label>
          ))}
        </div>
      )}

      {!canEdit ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Necesitas el permiso <code>p.edit</code> en este proyecto para cambiar sus vínculos.
        </p>
      ) : null}

      {error ? (
        <div className="mt-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export function LinkedProjectsSection(props: {
  /**
   * Render only this project's card, without the heading.
   *
   * Set when the panel is a tab inside Settings → Proyectos, where the project
   * is already chosen above and repeating the explanation on every tab would
   * turn the section into a wall of prose. Absent, it behaves as the standalone
   * screen it used to be.
   */
  readonly projectId?: ProjectId;
}) {
  const projects = useStore((state) => state.projects);
  const only = props.projectId;

  if (only !== undefined) {
    const project = projects.find((candidate) => candidate.id === only);
    if (project === undefined) return null;
    return (
      <div className="flex flex-col gap-4">
        <p className="text-xs text-muted-foreground">
          Un agente trabajando aquí puede delegar en los proyectos marcados: el hilo hijo se abre en
          el otro repositorio, con su propio worktree, su rama y su PR. El vínculo hace{" "}
          <em>posible</em> la delegación, nunca la autoriza, y va en un solo sentido — si quieres la
          vuelta, decláralo también en el otro proyecto.
        </p>
        <ProjectLinksCard
          hideName
          project={project}
          candidates={projects.filter((candidate) => candidate.id !== project.id)}
        />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        No hay proyectos todavía. Crea uno desde el sidebar y vuelve aquí para vincularlo con otro.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Proyectos vinculados</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Un agente trabajando en un proyecto puede delegar trabajo en los proyectos que este
          declare aquí: el hilo hijo se abre en el otro repositorio, con su propio worktree, su rama
          y su PR. El vínculo hace <em>posible</em> la delegación, nunca la autoriza — quien delega
          sigue necesitando permiso para crear hilos en el proyecto de destino.
        </p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Los vínculos van en un solo sentido. Si además quieres que los hilos del otro proyecto
          puedan trabajar aquí, decláralo también en su propia tarjeta.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {projects.map((project) => (
          <ProjectLinksCard
            key={project.id}
            project={project}
            candidates={projects.filter((candidate) => candidate.id !== project.id)}
          />
        ))}
      </div>
    </div>
  );
}
